import {
  Checkbox as AriaCheckbox,
  CheckboxProps as AriaCheckboxProps,
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  RadioGroupProps as AriaRadioGroupProps,
  RadioProps as AriaRadioProps,
  composeRenderProps,
} from "react-aria-components";
import { Check, Minus } from "lucide-react";
import { cn } from "@/utils/helpers";

/*
 * Checkbox and Radio (CreativeOS Library.lib.pen, Components / Inputs and
 * selection, style B). A checkbox (many of) is 18px at radius 5: unchecked has
 * no fill and a 1.5 edge, checked takes the accent fill with a 12px check and
 * no edge. A radio (one of) is a circle: picked is a 1.5 accent ring with a
 * centred accent dot. The two never look alike.
 */

/** The box alone, for rows that draw their own label (a picked option row). */
export function KitCheckboxBox({ checked, indeterminate }: { checked: boolean; indeterminate?: boolean }) {
  const on = checked || indeterminate;
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-[18px] shrink-0 place-items-center rounded-check transition-colors duration-[90ms]",
        on ? "bg-co-accent-fill text-co-on-accent" : "border-[1.5px] border-co-faint",
      )}
    >
      {indeterminate ? (
        <Minus strokeWidth={3} className="size-3" />
      ) : checked ? (
        <Check strokeWidth={3} className="co-tick-draw size-3" />
      ) : null}
    </span>
  );
}

export interface KitCheckboxProps extends AriaCheckboxProps {
  className?: string;
}

export function KitCheckbox({ children, className, ...props }: KitCheckboxProps) {
  return (
    <AriaCheckbox
      {...props}
      className={composeRenderProps(className, (cls, { isDisabled }) =>
        cn(
          "group inline-flex items-center gap-2.5 text-[13px] text-co-text2 cursor-pointer select-none outline-none",
          "data-[focus-visible]:[&>span:first-child]:outline-2 data-[focus-visible]:[&>span:first-child]:outline-offset-2 data-[focus-visible]:[&>span:first-child]:outline-co-accent",
          isDisabled && "opacity-50 cursor-not-allowed",
          cls,
        ),
      )}
    >
      {composeRenderProps(children, (kids, { isSelected, isIndeterminate }) => (
        <>
          <KitCheckboxBox checked={isSelected} indeterminate={isIndeterminate} />
          {kids != null ? <span className="leading-snug">{kids}</span> : null}
        </>
      ))}
    </AriaCheckbox>
  );
}

/** The radio circle alone, for rows that draw their own label. */
export function KitRadioMark({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-[18px] shrink-0 place-items-center rounded-full border-[1.5px] transition-colors duration-[160ms]",
        checked ? "border-co-accent" : "border-co-faint",
      )}
    >
      {checked ? <span className="co-dot-in size-2 rounded-full bg-co-accent" /> : null}
    </span>
  );
}

export function KitRadioGroup({ className, ...props }: AriaRadioGroupProps & { className?: string }) {
  return <AriaRadioGroup {...props} className={cn("flex flex-col gap-2", className)} />;
}

export function KitRadio({ children, className, ...props }: AriaRadioProps & { className?: string }) {
  return (
    <AriaRadio
      {...props}
      className={composeRenderProps(className, (cls, { isDisabled }) =>
        cn(
          "group inline-flex items-center gap-2.5 text-[13px] text-co-text2 cursor-pointer select-none outline-none",
          "data-[focus-visible]:[&>span:first-child]:outline-2 data-[focus-visible]:[&>span:first-child]:outline-offset-2 data-[focus-visible]:[&>span:first-child]:outline-co-accent",
          isDisabled && "opacity-50 cursor-not-allowed",
          cls,
        ),
      )}
    >
      {composeRenderProps(children, (kids, { isSelected }) => (
        <>
          <KitRadioMark checked={isSelected} />
          {kids != null ? <span className="leading-snug">{kids}</span> : null}
        </>
      ))}
    </AriaRadio>
  );
}
